import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMotorcycles, deleteMotorcycle } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Search, Trash2, Pencil, Filter, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';

export default function MotorcycleList() {
  const navigate = useNavigate();
  const [motorcycles, setMotorcycles] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [makeId, setMakeId] = useState('');
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const [makes, setMakes] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getMotorcycles({ page, limit: 10, makeId: makeId || undefined, search: searchTerm || undefined }),
      (async () => {
        try {
          const { getMakes } = await import('../../api');
          const makesData = await getMakes();
          setMakes(makesData);
        } catch {}
      })()
    ]).then(([data]) => {
      setMotorcycles(data?.motorcycles || data || []);
      setPagination(data?.pagination || { pages: 1 });
    }).finally(() => setLoading(false));
  }, [page, makeId, searchTerm]);

  const handleSearch = () => {
    setSearchTerm(search);
    setPage(1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this motorcycle?')) return;
    try {
      await deleteMotorcycle(id);
      setMotorcycles(motorcycles.filter(m => m._id !== id));
    } catch (err) {
      alert('Failed to delete motorcycle');
    }
  };

  const resetFilters = () => {
    setSearch('');
    setSearchTerm('');
    setMakeId('');
    setPage(1);
  };

  const hasActiveFilters = makeId || searchTerm;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Motorcycles</h1>
        <Button onClick={() => navigate('/admin/motorcycles/add')}>
          <Plus className="w-4 h-4 mr-2" />
          Add Motorcycle
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search motorcycles..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    className="pl-9"
                  />
                </div>
              </div>
              <Button onClick={handleSearch} variant="default">Search</Button>

              <Select value={makeId} onValueChange={v => { setMakeId(v); setPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Make" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Makes</SelectItem>
                  {makes.map(m => <SelectItem key={m._id} value={m._id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="outline" onClick={resetFilters}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Make</TableHead>
              <TableHead>Model</TableHead>
              <TableHead className="w-20">Year</TableHead>
              <TableHead className="w-36">Engine</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-sm text-slate-400">Loading...</TableCell>
              </TableRow>
            ) : motorcycles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-sm text-slate-400">No motorcycles found.</TableCell>
              </TableRow>
            ) : (
              motorcycles.map(m => (
                <TableRow key={m._id}>
                  <td className="font-medium text-slate-900">{m.make}</td>
                  <td className="text-slate-600">{m.model}</td>
                  <td>{m.year}</td>
                  <td className="text-slate-600">{m.displacement ? m.displacement + 'cc' : '-'}</td>
                  <td>
                    <Badge variant={m.isAvailable ? 'default' : 'destructive'}>
                      {m.isAvailable ? 'Available' : 'Unavailable'}
                    </Badge>
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-indigo-600"
                        onClick={() => navigate(`/admin/motorcycles/${m._id}/edit`)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600"
                        onClick={() => handleDelete(m._id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-1 px-6 py-4">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
              let pageNum;
              if (pagination.pages <= 5) pageNum = i + 1;
              else if (page <= 3) pageNum = i + 1;
              else if (page >= pagination.pages - 2) pageNum = pagination.pages - 4 + i;
              else pageNum = page - 2 + i;
              return (
                <Button key={pageNum} variant={page === pageNum ? 'default' : 'outline'} size="sm" className="h-8 w-8 p-0" onClick={() => setPage(pageNum)}>
                  {pageNum}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" disabled={page === pagination.pages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}